import React, { useEffect, useMemo, useRef } from "react";
import { FormGroup, FormLabel, FormControl } from "react-bootstrap";
import { FormattedMessage, IntlShape, MessageDescriptor, useIntl } from "react-intl";
import { UseFormReturn } from "react-hook-form";
import Selector from "../../../../components/selector";
import OptimizedMenuList from "../../../../components/selector/optimized-menu-list";
import { ErrorField } from "../../../shared/widgets/TranslationComponent/styles";
import { ScenarioEntity } from "../../scenario/domain/scenario.entity";
import {
  ConditionType,
  OrchestrationCriteria,
  OrchestrationCriteriaSchema,
} from "../../shared/domain/orchestrationCriteria.entity";
import { useConditionTypeLabels } from "../../orchestrator.messages";
import { ExperimentEntity, ExperimentInput, VariantType } from "../domain/experiment.entity";
import { compareScenarioCriteria, ComparabilityResult } from "../domain/scenarioComparability";
import { messages } from "../experiment.messages";
import { ScenarioDetails } from "./ScenarioDetail";
import ComparableOption, { ComparabilityOptionData } from "./ComparableOption";

export const ENGINE_DEFAULT_VALUE = "__ENGINE_DEFAULT__" as const;

interface SelectionIdentityVariant {
  scenarioId?: string | null;
  isEngineDefault?: boolean;
}

export function selectionIdentity(variant: SelectionIdentityVariant | undefined): string | null {
  if (!variant) return null;
  if (variant.isEngineDefault) return ENGINE_DEFAULT_VALUE;
  return variant.scenarioId ?? null;
}

const EMPTY_CRITERIA = OrchestrationCriteriaSchema.parse({});

/**
 * One shared sentence-building function for the preventive tooltip and the corrective backend-
 * rejection notice, so wording never drifts between the two layers (Business Rule 3). Only names
 * condition *types* — the backend never returns the actual differing values, only which types
 * differ/are missing/whose matchMode differs.
 */
export function formatComparabilityDetail(
  result: Extract<ComparabilityResult, { comparable: false }>,
  conditionTypeLabels: Record<ConditionType, string>,
  intl: IntlShape
): string {
  const clauses: string[] = [];
  for (const type of result.onlyInA) {
    clauses.push(
      intl.formatMessage(messages.validation.comparabilityOnlyInA, {
        label: conditionTypeLabels[type],
      })
    );
  }
  for (const type of result.onlyInB) {
    clauses.push(
      intl.formatMessage(messages.validation.comparabilityOnlyInB, {
        label: conditionTypeLabels[type],
      })
    );
  }
  for (const type of result.differentValue) {
    clauses.push(
      intl.formatMessage(messages.validation.comparabilityDifferentValue, {
        label: conditionTypeLabels[type],
      })
    );
  }
  if (result.matchModeDiffers) {
    clauses.push(intl.formatMessage(messages.validation.comparabilityMatchModeDiffers));
  }
  if (clauses.length <= 1) return clauses[0] ?? "";
  return intl.formatMessage(messages.common.listConjunction, {
    first: clauses.slice(0, -1).join(", "),
    last: clauses[clauses.length - 1],
  });
}

interface ScenarioOption extends ComparabilityOptionData {
  value: string;
  label: string;
  isDisabled?: boolean;
  disabledReason?: string;
}

interface OppositeVariantInfo {
  scenarioId: string | null;
  isEngineDefault: boolean;
}

interface ScenarioSelectCardProps {
  variantIndex: 0 | 1;
  titleMessage: MessageDescriptor;
  titleColor: string;
  namePlaceholder: MessageDescriptor;
  scenarios: ScenarioEntity[];
  disabledValue: string | null;
  form: UseFormReturn<ExperimentInput, unknown, ExperimentEntity>;
  scenariosForDetails: ScenarioEntity[];
  /** The opposite side's currently-selected identity, used to compute comparability-based
   * disabling against that side's criteria. `undefined` when nothing is selected there yet. */
  oppositeVariant: OppositeVariantInfo | undefined;
  /** When true, this card stays fully disabled (no option selectable) until `oppositeVariant` is
   * set - Scenario B cannot be picked before Scenario A (comparability filtering is one-way, so
   * only Scenario B's card ever passes this as true). */
  disabledUntilOppositeSelected?: boolean;
}

export function ScenarioSelectCard({
  variantIndex,
  titleMessage,
  titleColor,
  namePlaceholder,
  scenarios,
  disabledValue,
  form,
  scenariosForDetails,
  oppositeVariant,
  disabledUntilOppositeSelected,
}: ScenarioSelectCardProps) {
  const intl = useIntl();
  const conditionTypeLabels = useConditionTypeLabels();
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const hasBeenShownRef = useRef(false);

  const path = `variants.${variantIndex}` as const;

  // The opposite side's criteria, for comparability purposes: the synthetic engine-default
  // selection is treated as empty criteria (matchMode ALL, no conditions) — Business Rule 2.
  // `undefined` when nothing is selected yet on the opposite side, meaning no comparability-based
  // disabling should apply at all (Business Rule 5).
  const oppositeCriteria: OrchestrationCriteria | undefined = useMemo(() => {
    if (!oppositeVariant) return undefined;
    if (oppositeVariant.isEngineDefault) return EMPTY_CRITERIA;
    if (!oppositeVariant.scenarioId) return undefined;
    return (
      scenariosForDetails.find(s => s.id === oppositeVariant.scenarioId)?.criteria ?? EMPTY_CRITERIA
    );
  }, [oppositeVariant, scenariosForDetails]);

  const options: ScenarioOption[] = useMemo(
    () =>
      [
        {
          value: ENGINE_DEFAULT_VALUE,
          label: intl.formatMessage(messages.formLabels.defaultOption),
          criteria: EMPTY_CRITERIA,
        },
        ...scenarios.map(scenario => ({
          value: scenario.id,
          label: `${scenario.ticker} — ${scenario.name}`,
          criteria: scenario.criteria ?? EMPTY_CRITERIA,
        })),
      ].map(({ criteria, ...option }) => {
        const identityDisabled = disabledValue !== null && option.value === disabledValue;
        if (identityDisabled) {
          return { ...option, isDisabled: true };
        }
        if (!oppositeCriteria) {
          // Nothing selected on the opposite side yet — nothing to compare against.
          return option;
        }
        const result = compareScenarioCriteria(criteria, oppositeCriteria);
        if (result.comparable) {
          return option;
        }
        return {
          ...option,
          isDisabled: true,
          disabledReason: formatComparabilityDetail(result, conditionTypeLabels, intl),
        };
      }),
    [scenarios, intl, disabledValue, oppositeCriteria, conditionTypeLabels]
  );

  // "No comparable scenario available" (AC2) fires only when comparability is what's exhausting
  // the options - i.e. at least one option carries a `disabledReason` (comparability-based). Pure
  // identity-based exhaustion (e.g. Rule 9's "every real scenario already in use, only Default is
  // left, and it's mutually excluded" edge case) must keep rendering the Selector with its single
  // disabled option, unchanged from US-012 - it is a "nothing left to pick" situation, not a
  // "nothing is comparable" one.
  const allDisabled =
    options.length > 0 &&
    options.every(option => option.isDisabled) &&
    options.some(option => option.disabledReason);

  // Scenario B stays fully disabled until Scenario A has a selection - there is nothing yet to be
  // comparable with, so no option should be pickable (distinct from `allDisabled`, which fires once
  // Scenario A IS selected but nothing on this side matches it).
  const requiresOppositeFirst = !!disabledUntilOppositeSelected && !oppositeVariant;

  const variant = form.watch(`variants.${variantIndex}`);
  const selected = selectionIdentity(variant);
  const selectedOption = options.find(option => option.value === selected) ?? null;

  const { ref: registerRef, ...tickerField } = form.register(`variants.${variantIndex}.ticker`);

  useEffect(() => {
    if (selected && !hasBeenShownRef.current) {
      hasBeenShownRef.current = true;
      nameInputRef.current?.focus();
    }
    if (!selected) {
      hasBeenShownRef.current = false;
    }
  }, [selected]);

  function handleChange(option: ScenarioOption | null) {
    if (!option) {
      form.setValue(`${path}.scenarioId`, null, { shouldValidate: true, shouldDirty: true });
      form.setValue(`${path}.scenarioTicker`, null);
      form.setValue(`${path}.isEngineDefault`, false);
      form.setValue(`${path}.type`, VariantType.VARIANT_TYPE_SCENARIO);
      form.setValue(`${path}.url`, null);
      return;
    }
    if (option.value === ENGINE_DEFAULT_VALUE) {
      form.setValue(`${path}.scenarioId`, null, { shouldValidate: true, shouldDirty: true });
      form.setValue(`${path}.scenarioTicker`, "default");
      form.setValue(`${path}.isEngineDefault`, true);
      form.setValue(`${path}.type`, VariantType.VARIANT_TYPE_URL);
      form.setValue(`${path}.url`, ""); // placeholder — isEngineDefault variants are VARIANT_TYPE_URL, never SCENARIO (US-001)
    } else {
      const scenario = scenarios.find(s => s.id === option.value);
      form.setValue(`${path}.scenarioId`, option.value, {
        shouldValidate: true,
        shouldDirty: true,
      });
      form.setValue(`${path}.scenarioTicker`, scenario?.ticker ?? null);
      form.setValue(`${path}.isEngineDefault`, false);
      form.setValue(`${path}.type`, VariantType.VARIANT_TYPE_SCENARIO);
      form.setValue(`${path}.url`, null);
    }
  }

  const isOptionDisabled = (option: ScenarioOption) => !!option.isDisabled;

  const errors = form.formState.errors.variants?.[variantIndex];

  return (
    <>
      <h6 style={{ color: titleColor }}>
        <FormattedMessage {...titleMessage} />
      </h6>

      <FormGroup className="mb-3">
        <FormLabel>
          <FormattedMessage {...messages.formLabels.scenario} />
        </FormLabel>
        {allDisabled ? (
          <p className="text-muted mb-0">
            <FormattedMessage {...messages.formLabels.noComparableScenarios} />
          </p>
        ) : (
          <Selector
            options={options}
            value={selectedOption}
            onChange={handleChange}
            isOptionDisabled={isOptionDisabled}
            isDisabled={requiresOppositeFirst}
            placeholder={intl.formatMessage(messages.formLabels.selectScenarioPlaceholder)}
            components={{ Option: ComparableOption, MenuList: OptimizedMenuList }}
          />
        )}
        {/* `errors.scenarioId.message` is VariantSchema's internal superRefine string, written
            for developers and never translated — it must not reach the user. */}
        {errors?.scenarioId && (
          <ErrorField>{intl.formatMessage(messages.validation.scenarioRequired)}</ErrorField>
        )}
      </FormGroup>

      {!allDisabled && !requiresOppositeFirst && variant?.scenarioId && (
        <ScenarioDetails scenarioId={variant.scenarioId} scenarios={scenariosForDetails} />
      )}

      {!allDisabled && !requiresOppositeFirst && selected && (
        <FormGroup className="mb-3">
          <FormLabel htmlFor={`scenarioNameInput-${variantIndex}`}>
            <FormattedMessage {...messages.formLabels.scenarioName} />
          </FormLabel>
          <FormControl
            id={`scenarioNameInput-${variantIndex}`}
            type="text"
            placeholder={intl.formatMessage(namePlaceholder)}
            {...tickerField}
            ref={(el: HTMLInputElement | null) => {
              registerRef(el);
              nameInputRef.current = el;
            }}
          />
          <div className="text-muted small mt-1">
            <FormattedMessage {...messages.formLabels.scenarioNameHelp} />
          </div>
          {errors?.ticker && <ErrorField>{errors.ticker.message}</ErrorField>}
        </FormGroup>
      )}
    </>
  );
}
